import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

interface ModalState {
  userModal: { userId: number; eventId: number; initialTab?: string } | null
  transactionModal: { transactionId: number; eventId: number } | null
  orderModal: { orderId: number; eventId: number } | null
}

interface ModalContextType {
  openUserModal: (userId: number, eventId: number, initialTab?: string) => void
  openTransactionModal: (transactionId: number, eventId: number) => void
  openOrderModal: (orderId: number, eventId: number) => void
  closeAllModals: () => void
  modalState: ModalState
}

const ModalContext = createContext<ModalContextType | null>(null)

export function useModal() {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider')
  }
  return context
}

export function ModalProvider({ children }: { children: ReactNode }) {
  const [modalState, setModalState] = useState<ModalState>({
    userModal: null,
    transactionModal: null,
    orderModal: null,
  })

  const openUserModal = useCallback((userId: number, eventId: number, initialTab?: string) => {
    setModalState((prev) => ({ ...prev, userModal: { userId, eventId, initialTab } }))
  }, [])

  const openTransactionModal = useCallback((transactionId: number, eventId: number) => {
    setModalState((prev) => ({ ...prev, transactionModal: { transactionId, eventId } }))
  }, [])

  const openOrderModal = useCallback((orderId: number, eventId: number) => {
    setModalState((prev) => ({ ...prev, orderModal: { orderId, eventId } }))
  }, [])

  const closeAllModals = useCallback(() => {
    setModalState({
      userModal: null,
      transactionModal: null,
      orderModal: null,
    })
  }, [])

  return (
    <ModalContext.Provider
      value={{
        openUserModal,
        openTransactionModal,
        openOrderModal,
        closeAllModals,
        modalState,
      }}
    >
      {children}
    </ModalContext.Provider>
  )
}
